/**
 * Blog server functions. Thin wrappers only — runtime logic lives in
 * `blog.server.ts` so server-function splitting cannot drop it.
 */
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { BlogAuthor, BlogCategory, BlogPostInput, BlogTag } from "./types";

export const getBlogIndexFn = createServerFn({ method: "GET" }).handler(async () => {
  const s = await import("./blog.server");
  return s.fetchBlogIndex();
});

export const getBlogPostFn = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => {
    if (!input?.slug) throw new Error("slug required");
    return input;
  })
  .handler(async ({ data }) => {
    const s = await import("./blog.server");
    return s.fetchPublishedPost(data.slug);
  });

export const listAdminBlogPostsFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./blog.server");
    return s.fetchAllPosts();
  });

export const getAdminBlogPostFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => {
    if (!input?.id) throw new Error("id required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./blog.server");
    return s.fetchPostById(data.id);
  });

export const saveBlogPostFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: BlogPostInput) => {
    if (!input?.slug || !input?.title) throw new Error("slug and title required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./blog.server");
    return s.savePost(data, context.userId);
  });

export const deleteBlogPostFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => {
    if (!input?.id) throw new Error("id required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./blog.server");
    return s.deletePost(data.id);
  });

export const saveBlogCategoryFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: Partial<BlogCategory>) => {
    if (!input?.slug || !input?.name) throw new Error("slug and name required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./blog.server");
    return s.saveCategory(data);
  });

export const saveBlogTagFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: Partial<BlogTag>) => {
    if (!input?.slug || !input?.name) throw new Error("slug and name required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./blog.server");
    return s.saveTag(data);
  });

export const saveBlogAuthorFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: Partial<BlogAuthor>) => {
    if (!input?.slug || !input?.name) throw new Error("slug and name required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./blog.server");
    return s.saveAuthor(data);
  });

export const deleteBlogTaxonomyFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { kind: "category" | "tag" | "author"; id: string }) => {
    if (!input?.kind || !input?.id) throw new Error("kind and id required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const admin = await import("@/lib/admin/console.server");
    await admin.assertElevated(context as never);
    const s = await import("./blog.server");
    return s.deleteTaxonomy(data.kind, data.id);
  });